import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { logError, getErrorSummary } from "../../lib/utils";
import { getProductos, updateProducto, deleteProducto } from "../../services/divinaProductsService";
import { Button } from "../../components/ui/button";
import { KpiCard } from "../../components/ui/KpiCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { PlusCircle, Package, Building2, FileJson, Archive, Trash2, Settings } from "lucide-react";
import { toast } from "sonner";
import {
  GroupedTable,
  StatusTabs,
  GroupToggle,
  StateToggle,
  CompanyChip,
  StatusPill,
  RowActions,
  ActionButton,
} from "../../components/product_config/GroupedTable";
import { useSupabaseClient } from "../../lib/supabase";

const NONE_KEY = "__sin_compania__";

/* Tag compacto "JSON" — indica que el producto tiene config descripcion_json */
function ConfigTag() {
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 600,
        color: "#54585A", background: "#F1F3F4", borderRadius: 6, padding: "2px 7px", letterSpacing: "0.01em",
      }}
    >
      <Settings size={11} /> JSON
    </span>
  );
}

export default function DivinaProductsList() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("todos");
  const [groupBy, setGroupBy] = useState("compania");
  const [q, setQ] = useState("");
  const [updatingById, setUpdatingById] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productoToDelete, setProductoToDelete] = useState(null);
  const supabaseClient = useSupabaseClient();

  useEffect(() => {
    if (supabaseClient) loadProductos();
  }, [supabaseClient]);

  const loadProductos = async () => {
    try {
      setLoading(true);
      const data = await getProductos(supabaseClient);
      setProductos(data);
    } catch (error) {
      logError("Error loading productos:", error, { ignoreAbort: true });
    } finally {
      setLoading(false);
    }
  };

  /* ── Conteos para tabs ──────────────────────────────────────────────────── */
  const counts = useMemo(() => ({
    todos: productos.filter((p) => !p.archivado).length,
    activos: productos.filter((p) => p.activo === true && p.archivado !== true).length,
    inactivos: productos.filter((p) => p.activo === false && p.archivado !== true).length,
    archivados: productos.filter((p) => p.archivado === true).length,
  }), [productos]);

  /* ── KPIs ───────────────────────────────────────────────────────────────── */
  const kpis = useMemo(() => {
    const vivos = productos.filter((p) => !p.archivado);
    const companias = new Set(vivos.map((p) => p.compania?.nombre_corto).filter(Boolean)).size;
    const conJson = vivos.filter((p) => Boolean(p.descripcion_json)).length;
    return { companias, conJson };
  }, [productos]);

  /* ── Filtro por tab + búsqueda ──────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let out = productos.slice();
    if (tab === "activos") out = out.filter((p) => p.activo === true && p.archivado !== true);
    else if (tab === "inactivos") out = out.filter((p) => p.activo === false && p.archivado !== true);
    else if (tab === "archivados") out = out.filter((p) => p.archivado === true);
    else out = out.filter((p) => p.archivado !== true);

    if (q.trim()) {
      const n = q.trim().toLowerCase();
      out = out.filter((p) =>
        (p.nombre || "").toLowerCase().includes(n) ||
        (p.descripcion || "").toLowerCase().includes(n) ||
        (p.compania?.nombre_corto || "").toLowerCase().includes(n)
      );
    }
    return out;
  }, [productos, tab, q]);

  /* ── Handlers (mantienen la lógica previa, ahora optimistas) ────────────── */
  const handleStatusToggle = async (producto, newStatus) => {
    const previous = Boolean(producto.activo);
    const next = Boolean(newStatus);
    if (previous === next) return;
    try {
      setUpdatingById((s) => ({ ...s, [producto.id]: true }));
      setProductos((prev) => prev.map((p) => (p.id === producto.id ? { ...p, activo: next } : p)));
      await updateProducto(producto.id, { ...producto, activo: next }, supabaseClient);
    } catch (error) {
      const summary = getErrorSummary(error);
      console.error("Error updating producto status:", summary);
      setProductos((prev) => prev.map((p) => (p.id === producto.id ? { ...p, activo: previous } : p)));
      toast.error(`No se pudo actualizar el estado: ${summary.message}`);
    } finally {
      setUpdatingById((s) => ({ ...s, [producto.id]: false }));
    }
  };

  const handleArchiveToggle = async (producto, archived) => {
    const previous = Boolean(producto.archivado);
    const next = Boolean(archived);
    if (previous === next) return;
    try {
      setUpdatingById((s) => ({ ...s, [producto.id]: true }));
      setProductos((prev) => prev.map((p) => (p.id === producto.id ? { ...p, archivado: next } : p)));
      await updateProducto(producto.id, { ...producto, archivado: next }, supabaseClient);
    } catch (error) {
      const summary = getErrorSummary(error);
      console.error("Error updating producto archive status:", summary);
      setProductos((prev) => prev.map((p) => (p.id === producto.id ? { ...p, archivado: previous } : p)));
      toast.error(`No se pudo archivar el producto: ${summary.message}`);
    } finally {
      setUpdatingById((s) => ({ ...s, [producto.id]: false }));
    }
  };

  const handleDeleteClick = (producto) => {
    setProductoToDelete(producto);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!productoToDelete) return;
    try {
      await deleteProducto(productoToDelete.id, supabaseClient);
      setProductos((prev) => prev.filter((p) => p.id !== productoToDelete.id));
      setDeleteDialogOpen(false);
      setProductoToDelete(null);
    } catch (error) {
      console.error("Error deleting producto:", error);
      toast.error(`No se pudo eliminar el producto: ${getErrorSummary(error).message}`);
    }
  };

  /* ── Columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const cols = [
      {
        key: "nombre",
        label: "Producto",
        minWidth: 240,
        cellStyle: { whiteSpace: "normal" },
        render: (r) => (
          <div style={{ minWidth: 0, lineHeight: 1.3 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Link to={`/product_config/productos/edit/${r.id}`} title="Editar producto" style={{ fontSize: 13.5, fontWeight: 600, color: "#007D6E", textDecoration: "none", cursor: "pointer" }}>{r.nombre}</Link>
              {r.descripcion_json && <ConfigTag />}
            </div>
            {r.descripcion && (
              <div style={{ fontSize: 11.5, color: "#8D8D8D", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 420 }}>
                {r.descripcion}
              </div>
            )}
          </div>
        ),
      },
    ];

    if (groupBy === "ninguno") {
      cols.push({
        key: "comp",
        label: "Compañía",
        width: 200,
        render: (r) => <CompanyChip name={r.compania?.nombre_corto} />,
      });
    }

    cols.push({
      key: "estado",
      label: "Estado",
      width: 150,
      render: (r) =>
        r.archivado ? (
          <StatusPill estado="archivado" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StateToggle on={r.activo} disabled={Boolean(updatingById[r.id])} onChange={(v) => handleStatusToggle(r, v)} />
            <StatusPill estado={r.activo ? "activo" : "inactivo"} />
          </div>
        ),
    });

    cols.push({
      key: "_acc",
      label: "",
      width: 130,
      align: "right",
      render: (r) => (
        <RowActions>
          <ActionButton icon={Settings} title="Configurar" to={`/product_config/productos/edit/${r.id}`} />
          {!r.archivado && (
            <ActionButton icon={Archive} title="Archivar" disabled={Boolean(updatingById[r.id])} onClick={() => handleArchiveToggle(r, true)} />
          )}
          <ActionButton icon={Trash2} title="Eliminar" danger onClick={() => handleDeleteClick(r)} />
        </RowActions>
      ),
    });

    return cols;
  }, [groupBy, updatingById]);

  /* ── Grupos por compañía (con compañía primero, alfabético) ─────────────── */
  const groups = useMemo(() => {
    const decorate = (p) => ({ ...p, _dim: !p.archivado && p.activo === false });

    if (groupBy === "ninguno") {
      return [{ id: "all", title: "Todos los productos", count: filtered.length, unit: "producto", unitPlural: "productos", rows: filtered.map(decorate) }];
    }

    const byComp = {};
    filtered.forEach((p) => {
      const key = p.compania?.nombre_corto || NONE_KEY;
      (byComp[key] = byComp[key] || []).push(p);
    });

    const keys = Object.keys(byComp).sort((a, b) => {
      if (a === NONE_KEY) return 1;
      if (b === NONE_KEY) return -1;
      return a.localeCompare(b, "es");
    });

    return keys.map((key) => {
      const list = byComp[key];
      const activos = list.filter((p) => p.activo === true && !p.archivado).length;
      return {
        id: key,
        title: key === NONE_KEY ? "Sin compañía" : key,
        count: list.length,
        unit: "producto",
        unitPlural: "productos",
        meta: <span>{activos} {activos === 1 ? "activo" : "activos"}</span>,
        rows: list.map(decorate),
      };
    });
  }, [filtered, groupBy]);

  return (
    <div className="@container/main p-6 space-y-6 max-w-[1200px]">
      {/* Encabezado */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[27px] font-bold tracking-[-0.03em] text-[#1A1A1A] dark:text-white">Productos</h1>
          <p className="text-sm text-[#54585A] dark:text-gray-400 mt-1">Gestión de productos de Divina por compañía aseguradora</p>
        </div>
        <Link to="/product_config/productos/new">
          <Button className="flex items-center gap-2">
            <PlusCircle className="h-4 w-4" />
            Nuevo producto
          </Button>
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-[14px] md:grid-cols-3">
        <KpiCard label="Productos activos" value={String(counts.activos)} icon={<Package />} badge={{ text: `de ${counts.todos} totales`, direction: "neu" }} />
        <KpiCard label="Compañías con producto" value={String(kpis.companias)} icon={<Building2 />} badge={{ text: "aseguradoras", direction: "neu" }} />
        <KpiCard label="Con configuración" value={String(kpis.conJson)} icon={<FileJson />} badge={{ text: "tienen JSON", direction: "neu" }} />
      </div>

      {/* Tabs de estado */}
      <StatusTabs
        active={tab}
        onChange={setTab}
        tabs={[
          { value: "todos", label: "Todos", count: counts.todos },
          { value: "activos", label: "Activos", count: counts.activos },
          { value: "inactivos", label: "Inactivos", count: counts.inactivos },
          { value: "archivados", label: "Archivados", count: counts.archivados },
        ]}
      />

      {loading ? (
        <div className="grid grid-cols-1 gap-[14px] md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-[#E0E0E0] bg-white p-5 space-y-3 animate-pulse">
              <div className="h-4 bg-slate-100 rounded w-1/2" />
              <div className="h-8 bg-slate-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : (
        <GroupedTable
          columns={columns}
          groups={groups}
          search="Buscar producto o compañía…"
          q={q}
          setQ={setQ}
          groupToggle={
            <GroupToggle
              label="Agrupar:"
              value={groupBy}
              onChange={setGroupBy}
              options={[{ value: "compania", label: "Por compañía" }, { value: "ninguno", label: "Ninguno" }]}
            />
          }
          totalShown={filtered.length}
          totalAll={counts.todos}
          empty={{ title: "Sin productos", text: "No hay productos que coincidan con este filtro." }}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el producto "{productoToDelete?.nombre}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
